import React, { useContext, useEffect, useState } from 'react';
import { ConfigContext } from "../../App";
import parse from 'html-react-parser';
import { HideFooter } from '../../utils';

export default function TeamsLanding() {
    const locales = useContext(ConfigContext).appConfig.languageData;
    const [hasHash, setHasHash] = useState(false);

    // Ensure header and footer are visible when component mounts
    useEffect(() => {
        HideFooter(false);
    }, []);

    // Handle anchor scrolling when component mounts
    useEffect(() => {
        // Check if there's a hash and update state
        setHasHash(!!window.location.hash);
        
        const handleHashScroll = () => {
            const hash = window.location.hash;
            setHasHash(!!hash); // Update state when hash is found
            if (hash) {
                const element = document.querySelector(hash);
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                }
            }
        };

        // Wait for complete page load including all resources
        if (document.readyState === 'complete') {
            setTimeout(handleHashScroll, 500);
        } else {
            const handleLoad = () => {
                setTimeout(handleHashScroll, 500);
            };
            
            window.addEventListener('load', handleLoad);
            
            return () => {
                window.removeEventListener('load', handleLoad);
            };
        }
    }, []);


    return (
        <>
            <div className="teams-landing">
                <div className="teams-banner">
                    <div className="landing-screen-row">
                        <div className="landing-screen-image-column">
                            <div className="landing-screen-image" role="presentation"></div>
                        </div>
                        <div className="landing-screen-text-column">
                            <p className="stay-up-to-date">{locales.stay_up_to_date}</p>
                            <h1>{locales.web_title}</h1>
                            <p className="about-status-adobe">
                                {locales.about_status_adobe ? parse(locales.about_status_adobe) : locales.about_status_adobe}
                                <div>
                                    <h3><span className='teams-icon'>{locales.about_status_teams_title ? parse(locales.about_status_teams_title) : locales.about_status_teams_title}</span></h3>
                                    <span>{locales.about_status_teams_p2}</span>
                                </div>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Setup Section */}
                <h1 className="on-boarding-header">{locales.teams_setup_section_header}</h1>
                
                <div id="setup-step1" className="landing-onboarding-steps-screen-row">
                    <div className="landing-onboarding-steps-screen-text-column-step1">
                        <p className='prerequisite'>{parse(locales.teams_onboarding_prerequisite + '<br/>' + locales.teams_onboarding_prerequisite_new_channel)}</p>
                        <p className="step1-text">{locales.teams_onboarding_first_step}</p>
                        <p className="step-header">{locales.teams_onboarding_first_step_header}</p>
                        <ul>
                            <li>{parse(locales.click_on_edit_subscriptions)}</li>
                            <li>{locales.choose_your_pp_and_po}</li>
                            <li>{locales.choose_your_prl_and_et}</li>
                            <li>{locales.select_teams_as_preferrred}</li>
                            <li>{locales.add_teams_Id_and_channel}</li>
                            <li>{locales.confirm_and_save_teams}</li>
                        </ul>
                    </div>
                    <div className="landing-onboarding-steps-screen-image-column-step1">
                        <div className="customize-your-sub-image" role="img" aria-label="Screenshot of the Edit Subscriptions screen for adding Microsoft Teams as a notification channel"></div>
                    </div>
                </div>

                {/* Installation Section */}
                <div id="setup-step2" className="landing-onboarding-steps-screen-row">
                    <div className="landing-onboarding-steps-screen-text-column-step2">
                        <p className="step2-text">{locales.teams_onboarding_second_step}</p>
                        <p className="step-header">{locales.teams_onboarding_second_step_header}</p>
                        <ul>
                            <li>{locales.teams_install_step_3 ? parse(locales.teams_install_step_3) : locales.teams_install_step_3}
                                <ul>
                                        <li>{locales.teams_install_step_3_sub1}</li>
                                        <li>{locales.teams_install_step_3_sub2}</li>
                                        <li>{locales.teams_install_step_3_sub3}</li>
                                </ul>
                            </li>
                            <li>{locales.teams_install_step_4 ? parse(locales.teams_install_step_4) : locales.teams_install_step_4}</li>
                        </ul>
                    </div>
                    <div className="landing-onboarding-steps-screen-image-column-step2">
                        <div className="teams-install-image" role="img" aria-label="Screenshot of the Open Microsoft Teams prompt after approving the Adobe Status app"></div>
                    </div>
                </div>

                {/* Configuration Section */}
                <div id="setup-step3" className="landing-onboarding-steps-screen-row">
                    <div className="landing-onboarding-steps-screen-text-column-step3">
                        <p className="step3-text">{locales.teams_onboarding_third_step}</p>
                        <p className="step-header">{locales.teams_onboarding_third_step_header}</p>
                        <ul>
                            <li>{locales.teams_install_step_5 ? parse(locales.teams_install_step_5) : locales.teams_install_step_5}</li>
                            <li>{locales.teams_setup_step_1 ? parse(locales.teams_setup_step_1) : locales.teams_setup_step_1}
                                <ul>
                                    <li>{locales.teams_setup_step_2 ? parse(locales.teams_setup_step_2) : locales.teams_setup_step_2}</li>
                                    <li>{locales.teams_setup_step_3 ? parse(locales.teams_setup_step_3) : locales.teams_setup_step_3}</li>
                                    <li>{locales.teams_setup_step_4 ? parse(locales.teams_setup_step_4) : locales.teams_setup_step_4}</li>
                                </ul>
                            </li>
                            <li>{locales.teams_setup_step_6 ? parse(locales.teams_setup_step_6) : locales.teams_setup_step_6}</li>
                            <li>{locales.teams_setup_step_5 ? parse(locales.teams_setup_step_5) : locales.teams_setup_step_5}</li>
                        </ul>
                    </div>
                    <div className="landing-onboarding-steps-screen-image-column-step3">
                        <div className="teams-setup-image-container">
                            <div className="teams-setup-image teams-setup-image-1" role="img" aria-label="Screenshot of the Microsoft Teams channel selection window shown after installing the Adobe Status app"></div>
                            <div className="teams-setup-image teams-setup-image-2" role="img" aria-label="Screenshot of the Microsoft Teams confirmation message after adding the Adobe Status app to a channel"></div>
                        </div>
                    </div>
                </div>

                <div id="setup-step4" className="landing-onboarding-steps-screen-row">
                    <div className="landing-onboarding-steps-screen-text-column-step4">
                        <p className="step4-text">{locales.teams_onboarding_fourth_step}</p>
                        <p className="step-header">{locales.teams_onboarding_fourth_step_header}</p>
                        <ul>
                            <li>{locales.teams_notification_step_1 ? parse(locales.teams_notification_step_1) : locales.teams_notification_step_1}</li>
                        </ul>
                    </div>
                    <div className="landing-onboarding-steps-screen-image-column-step4">
                        <div className="teams-notification-image" role="img" aria-label="Screenshot of an Adobe Status notification posted in a Microsoft Teams channel"></div>
                    </div>
                </div>

                {/* Removal Section */}
                <h1 className="on-boarding-header removal-section-header">{locales.teams_removal_section_header}</h1>
                
                <div id="remove-step1" className="landing-onboarding-steps-screen-row">
                    <div className="landing-onboarding-removal-steps-screen-text-column-step1">
                        <p className="removal-step1-text">{locales.teams_app_removal_step_1}</p>
                        <p className="step-header">{locales.teams_app_removal_step_header_1}</p>
                        <ul>
                            <li>{parse(locales.click_on_edit_subscriptions)}</li>
                            <li>{locales.choose_your_pp_and_po}</li>
                            <li>{locales.choose_your_prl_and_et}</li>
                            <li>{locales.teams_app_disable_step_1 ? parse(locales.teams_app_disable_step_1) : locales.teams_app_disable_step_1}</li>
                            <li>{locales.teams_app_disable_step_2 ? parse(locales.teams_app_disable_step_2) : locales.teams_app_disable_step_2}</li>
                            <li>{locales.teams_app_disable_step_3 ? parse(locales.teams_app_disable_step_3) : locales.teams_app_disable_step_3}</li>
                        </ul>
                    </div>
                    <div className="landing-onboarding-removal-steps-screen-image-column-step1">
                        <div className="teams-remove-image" role="img" aria-label="Screenshot of unselecting Teams as the preferred notification method in subscription preferences"></div>
                    </div>
                </div>
                <div id="remove-step2" className="landing-onboarding-steps-screen-row">
                    <div className="landing-onboarding-removal-steps-screen-text-column-step2">
                        <p className="removal-step2-text">{locales.teams_app_removal_step_2}</p>
                        <p className="step-header">{locales.teams_app_removal_step_header_2}</p>
                        <ul>
                            <li>{locales.teams_remove_step_1 ? parse(locales.teams_remove_step_1) : locales.teams_remove_step_1}</li>
                            <li>{locales.teams_remove_step_2 ? parse(locales.teams_remove_step_2) : locales.teams_remove_step_2}</li>
                            <li>{locales.teams_remove_step_3 ? parse(locales.teams_remove_step_3) : locales.teams_remove_step_3}</li>
                            <li>{locales.teams_remove_step_4 ? parse(locales.teams_remove_step_4) : locales.teams_remove_step_4}</li>
                            <li>{locales.teams_remove_step_5 ? parse(locales.teams_remove_step_5) : locales.teams_remove_step_5}</li>
                            <li>{locales.teams_app_removal_step_3_message ? parse(locales.teams_app_removal_step_3_message) : locales.teams_app_removal_step_3_message}</li>
                        </ul>
                    </div>
                    <div className="landing-onboarding-removal-steps-screen-image-column-step2">
                        <div className="teams-remove-image" role="img" aria-label="Screenshot of removing the Adobe Status app from a Microsoft Teams channel"></div>
                    </div>
                </div>
            </div>
        </>
    )
}
